import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import UIButton from 'Components/UIComponents/UIButton';
import UIPaginator from 'Components/UIComponents/UIPaginator';
import { useDataOrdering, usePagination } from 'Hooks/Common';
import { useRequestHistoryGet } from 'Hooks/RequestHistory';
import { ApiKey } from 'Interfaces/ApiKey';
import { OrderingDirection } from 'Interfaces/Common';
import { AuthorizedRoutePaths, settingsRedirectionMap } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';
import {
  selectRequestHistory,
  selectRequestHistoryPaginationData,
} from 'Utils/selectors';

import RequestHistoryList from './components/RequestHistoryList';

type selectedPage = { selected: number };

type ApiKeyParams = {
  apiKeyId: ApiKey['id'];
};

const RequestHistory = () => {
  const navigate = useNavigate();
  const params = useParams<ApiKeyParams>();

  const requestHistory = useSelector(selectRequestHistory);

  const [getRequestHistory, isRequestHistoryLoading] = useRequestHistoryGet();
  const [paginationProps, setPageNumber] = usePagination({
    paginationSelector: selectRequestHistoryPaginationData,
  });
  const { requestOrdering, orderingConfig } = useDataOrdering(requestHistory, {
    key: 'createdAt',
    direction: OrderingDirection.DESC,
  });

  const apiKeyId = useMemo(() => params.apiKeyId, [params]);

  const handleChangePage = (page: selectedPage) => {
    setPageNumber(page.selected);
  };

  const handleGetRequestHistory = useCallback(async () => {
    if (!apiKeyId) {
      navigate(AuthorizedRoutePaths.BASE_PATH);
      return;
    }

    const { key, direction } = orderingConfig;
    const orderingDirection = direction.toString().toUpperCase();
    try {
      getRequestHistory({
        limit: paginationProps.itemsLimit,
        page: paginationProps.pageNumber + 1,
        orderingKey: key,
        orderingDirection,
        apiKeyId,
      });
    } catch (error) {
      Toast.handleErrors(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationProps.itemsLimit, paginationProps.pageNumber, orderingConfig]);

  useEffect(() => {
    return () => setPageNumber(0);
  }, [setPageNumber]);

  useEffect(() => {
    handleGetRequestHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGetRequestHistory]);

  return (
    <div className="documents__wrapper">
      <div className="team__header-container">
        <div className="company__billet-container">
          <p className="team__header-title">Request History</p>
        </div>
        <UIButton
          className="apiKeys__back-button"
          priority="secondary"
          handleClick={() =>
            navigate(settingsRedirectionMap[AuthorizedRoutePaths.SETTINGS_API])
          }
          title="Back"
        />
      </div>
      <RequestHistoryList
        requestHistory={requestHistory}
        requestOrdering={requestOrdering}
        isLoading={isRequestHistoryLoading}
        paginationProps={paginationProps}
      />
      {paginationProps.pageCount > 1 && (
        <UIPaginator
          initialPage={paginationProps.pageNumber}
          pageCount={paginationProps.pageCount}
          onPageChange={handleChangePage}
        />
      )}
    </div>
  );
};

export default RequestHistory;
